import React, { useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GridColDef, GridSortModel } from "@mui/x-data-grid-pro";
import CustomDataGrid from "../../../../components/Shared/Table/CustomDataGrid";
import DeviceDrawer from "../../../../components/Drawers/DeviceDrawer";
import {
  useGetCellsSummaryQuery,
  useGetDrawerCountsMutation,
  useGetAlarmsByDeviceKeyMutation,
} from "../../../../store/api/devices/deviceApiSlice";
import { useGetDeviceFullDetailMutation } from "../../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useStoreDispatch } from "../../../../store/hooks";
import { setIsLoadingStatus, setOpenToast } from "../../../../store/helpers/helperReducers";
import {
  setOpenDeviceDrawer,
  setDrawerCounts,
  setSelectedDevice,
  setSelectedDeviceAlarms,
  setSelectedDeviceAlarmCount,
} from "../../../../store/api/devices/reducers";
import { Icon } from "../../../../components/Shared/Icon";
import { Button } from "../../../../components/ui/button";
import { errorMessage } from "../../../../utils/helpers";
import { ICellsSummaryModel } from "../../../../store/api/devices/types";
import { useSelector } from "react-redux";
import { selectedGeoRegionIdsSelector } from "../../../../store/api/admin/selectors";

const TABLE_ID = "cellsTable";

interface CellsTableProps {
  selectedGeoRegions: string[];
}

interface ToolbarProps {
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  exportHandler: () => void;
}

const DataGridToolbar: React.FC<ToolbarProps> = ({ searchText, setSearchText, exportHandler }) => (
  <div className="flex items-center justify-between">
    <div className="relative w-80">
      <SearchIcon
        sx={{ fontSize: 18 }}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
      />
      <input
        type="text"
        placeholder="Search devices..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring transition duration-200"
        style={{
          backgroundColor: "var(--card)",
          color: "var(--foreground)",
          fontSize: "0.875rem",
          height: 36,
        }}
      />
    </div>
    <Button
      variant="outline"
      size="icon"
      className="h-9 w-9 shrink-0"
      onClick={exportHandler}
      aria-label="Export CSV"
    >
      <Icon name="download" size={18} />
    </Button>
  </div>
);

const CellsTable: React.FC<CellsTableProps> = ({ selectedGeoRegions }) => {
  const dispatch = useStoreDispatch();
  const geoRegions = useSelector(selectedGeoRegionIdsSelector) || [];

  const [searchText, setSearchText] = useState("");
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "notOperationalPercent", sort: "desc" }]);
  const [rowClicked, setRowClicked] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getDeviceFullDetail] = useGetDeviceFullDetailMutation();
  const [getAlarmsByDeviceKey] = useGetAlarmsByDeviceKeyMutation();

  const { data, isLoading } = useGetCellsSummaryQuery({
    geoRegions: geoRegions.length > 0 ? geoRegions : selectedGeoRegions,
    size: 1000,
  }, { pollingInterval: 60000 });

  const filteredRows = useMemo<ICellsSummaryModel[]>(() => {
    if (!data?.content) return [];
    let rows = data.content.filter((r) => (r.notOperationalCells ?? 0) > 0);
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      rows = rows.filter((r) => r.deviceName?.toLowerCase().includes(lower));
    }
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      const dir = sort === "desc" ? -1 : 1;
      rows = [...rows].sort((a, b) => {
        const aVal = (a as any)[field];
        const bVal = (b as any)[field];
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === "string") return aVal.localeCompare(bVal) * dir;
        return (aVal - bVal) * dir;
      });
    }
    return rows;
  }, [data, searchText, sortModel]);

  const openDeviceDrawerHandler = async (row: ICellsSummaryModel) => {
    // deviceObjectKey is already the stripped device key (e.g. "12354C00518")
    const objectKey = row.deviceObjectKey || row.deviceName;
    if (!objectKey) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: "No device identifier found" }));
      return;
    }
    setRowClicked(true);
    setSelectedRowData({
      ...row,
      name: row.deviceName,
      objectKey,
    });
    dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: true, isLoadingAlarms: true }));
    dispatch(setOpenDeviceDrawer(true));
    try {
      const args = { filters: `key=objectKey&value=${objectKey}&weightType=FULL`, objectKey };
      const [drawerCountsResponse, deviceDetailResponse, alarmResponse] = await Promise.all([
        getDrawerCounts({ deviceKey: objectKey, filters: "" }),
        getDeviceFullDetail(args),
        getAlarmsByDeviceKey({ deviceKey: objectKey, filters: "?sort=retention%2Cdesc&page=0&size=50" }),
      ]);
      if ("error" in drawerCountsResponse || "error" in deviceDetailResponse || "error" in alarmResponse) {
        const err =
          "error" in drawerCountsResponse
            ? drawerCountsResponse.error
            : "error" in deviceDetailResponse
            ? deviceDetailResponse.error
            : (alarmResponse as any).error;
        throw err;
      }
      const { data: configCount } = drawerCountsResponse;
      const { data: deviceData } = deviceDetailResponse as any;
      const { data: alarmData } = alarmResponse as any;
      if (configCount && deviceData?.data?.[0] && alarmData) {
        dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
        dispatch(setDrawerCounts(configCount));
        dispatch(setSelectedDevice({ ...deviceData.data[0], ...deviceData?.deviceParams }));
        dispatch(setSelectedDeviceAlarms(alarmData.data));
        dispatch(setSelectedDeviceAlarmCount(alarmData.totalItems || 0));
      }
    } catch (error: any) {
      dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
      dispatch(setOpenDeviceDrawer(false));
      setRowClicked(false);
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleCloseDrawer = () => {
    setRowClicked(false);
  };

  const exportHandler = () => {
    const headers = ["Device Name", "Total Cells", "Operational Cells", "Non Operational Cells", "Non Operational Cells %"];
    const csvContent = [
      headers.join(","),
      ...filteredRows.map((r) =>
        [`"${r.deviceName}"`, r.operationalCells + r.notOperationalCells, r.operationalCells, r.notOperationalCells, r.notOperationalPercent.toFixed(1)].join(",")
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "CellsSummary.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = useMemo<GridColDef[]>(
    () => [
      {field: "deviceName", headerName: "Device Name", width: 264, sortable: true,
        renderCell: (cellValues: any) => (
          <span
            onClick={() => openDeviceDrawerHandler(cellValues.row)}
            className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer"
          >
            <span className="font-medium truncate group-hover/devlink:underline">{cellValues.value}</span>
            <Icon
              name="open_in_new"
              size={14}
              className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
            />
          </span>
        ),
      },
      {
        field: "totalCells",
        headerName: "Total Cells",
        width: 160,
        type: "number",
        sortable: true,
        align: "left",
        headerAlign: "left",
        valueGetter: (params: any) => params.row.operationalCells + params.row.notOperationalCells,
      },
      {
        field: "operationalCells",
        headerName: "Operational Cells",
        width: 192,
        type: "number",
        sortable: true,
        align: "left",
        headerAlign: "left",
      },
      {
        field: "notOperationalCells",
        headerName: "Non Operational Cells",
        width: 228,
        type: "number",
        sortable: true,
        align: "left",
        headerAlign: "left",
      },
      {
        field: "notOperationalPercent",
        headerName: "Non Operational Cells %",
        width: 240,
        type: "number",
        sortable: true,
        align: "left",
        headerAlign: "left",
        renderCell: (cellValues: any) => `${cellValues.value.toFixed(1)}%`,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      {rowClicked && selectedRowData && (
        <DeviceDrawer data={selectedRowData} closedButtonClicked={handleCloseDrawer} />
      )}
      <div className="w-full">
        {isLoading && !data ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <CircularProgress size={24} />
          </div>
        ) : (
          <>
            <h4 className="text-2xl font-bold my-0" style={{ color: "var(--foreground)" }}>
              {filteredRows.reduce((sum, r) => sum + (r.notOperationalCells ?? 0), 0)}
            </h4>
            <div className="mt-4 space-y-4">
              <DataGridToolbar
                searchText={searchText}
                setSearchText={setSearchText}
                exportHandler={exportHandler}
              />
              <CustomDataGrid
                tableId={TABLE_ID}
                rows={filteredRows}
                columns={columns}
                rowCount={filteredRows.length}
                loading={isLoading}
                disableColumnMenu
                getRowId={(row: any) => row.deviceName}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                paginationMode="client"
                sx={{ height: 600 }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CellsTable;
